import { revalidateLogic, useForm } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { setSessionToken } from '~/features/auth'
import { LinkButton } from '~/features/navigation'
import { queryClient, trpcProxy, useTRPC } from '~/services/trpc'
import { SetupWizard } from '~/templates/wizard/presets/setup'

import { Alert, Button, Field, FieldError, Input } from '@hap/components'

import { checkValidity } from './-utils'

export const Route = createFileRoute('/_public/signup/organization')({
  validateSearch: z.object({
    token: z.string(),
    email: z.email().optional(),
  }),
  component: RouteComponent,
})

const formSchema = z.object({
  organizationName: z
    .string()
    .min(3, {
      message: "Please enter your company or team's name. Must be at least 3 characters.",
    })
    .max(100, { message: 'Your organization name must be less than 100 characters' }),
  alias: z.string().min(3, { message: 'Please enter an alias' }),
})

function RouteComponent() {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [sessionExpired, setSessionExpired] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)

  const createMutation = useMutation(trpc.signup.createOrganization.mutationOptions())
  const resendMutation = useMutation(trpc.signup.sendCode.mutationOptions())

  const form = useForm({
    defaultValues: {
      organizationName: '',
      alias: '',
    },
    onSubmit: async ({ value }) => {
      if (sessionExpired) return

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      await createMutation
        .mutateAsync({
          signupToken: search.token,
          name: value.organizationName,
          alias: value.alias,
          timezone,
        })
        .then((response) => {
          setSessionToken(response.token)
          navigate({
            to: '/$alias/setup/upload',
            params: { alias: response.organizationAlias },
            reloadDocument: true,
            replace: true,
          })
        })
        .catch((error) => {
          if (error?.data?.code === 'UNAUTHORIZED') {
            setSessionExpired(true)
            return
          }
        })
    },
    validationLogic: revalidateLogic(),
    validators: {
      onChange: formSchema,
    },
  })

  const { data: sessionVerification, isLoading: isVerifyingSession } = useQuery(
    trpc.signup.verifySession.queryOptions({ signupToken: search.token }),
  )

  const verifiedEmail = sessionVerification?.email || search.email || null

  useEffect(() => {
    if (!sessionVerification) return
    setSessionExpired(!sessionVerification.valid)
  }, [sessionVerification])

  const handleResendCode = async () => {
    if (!verifiedEmail) return

    setResendError(null)
    await resendMutation
      .mutateAsync({ email: verifiedEmail.toLowerCase() })
      .then(() => {
        navigate({ to: '/signup/code', search: { email: verifiedEmail.toLowerCase() } })
      })
      .catch(() => {
        setResendError('Unable to send a new code right now. Please try again.')
      })
  }

  return (
    <SetupWizard title="Setup Organization" currentStep={1}>
      <section className="bg-accent grid max-w-prose gap-8 p-20">
        {isVerifyingSession ? (
          <h1 className="text-center text-4xl font-bold">
            Checking your signup session...
          </h1>
        ) : sessionExpired ? (
          <div className="grid gap-4">
            <h1 className="text-center text-4xl font-bold">
              Your signup session expired.
            </h1>
            {verifiedEmail && (
              <>
                <Button
                  type="button"
                  size="lg"
                  variant="lime"
                  loading={resendMutation.isPending}
                  loadingLabel="Sending code..."
                  onClick={handleResendCode}
                >
                  Send a new code to {verifiedEmail}
                </Button>
              </>
            )}
            {resendError && <Alert variant="warn">{resendError}</Alert>}
            <LinkButton to="/signup" variant="secondary">
              Restart
            </LinkButton>
          </div>
        ) : (
          <>
            <h1 className="mb-10 text-center text-5xl font-bold">
              What&apos;s the name of your company or team?
            </h1>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void form.handleSubmit()
              }}
              className="grid gap-4"
            >
              <form.Field
                name="organizationName"
                listeners={{
                  onChange: ({ value }) => {
                    form.setFieldValue('alias', normalizeAlias(value))
                  },
                }}
                children={(field) => {
                  const isInvalid = checkValidity({ field, form })
                  return (
                    <Field data-invalid={isInvalid}>
                      <Input
                        name={field.name}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        placeholder="e.g. Pendant Publishing"
                        aria-invalid={isInvalid}
                        variant="inverse"
                        size="lg"
                        autoComplete="off"
                      />
                      {isInvalid && <FieldError errors={field.state.meta.errors} />}
                    </Field>
                  )
                }}
              />

              <form.Field
                name="alias"
                validators={{
                  onDynamicAsyncDebounceMs: 500,
                  onDynamicAsync: async ({ value }) => {
                    const response = await queryClient.fetchQuery(
                      trpcProxy.signup.checkAlias.queryOptions({ alias: value }),
                    )

                    console.log(
                      response,
                      !response.available
                        ? { alias: 'Alias is already taken' }
                        : undefined,
                    )

                    return response.available
                      ? undefined
                      : {
                          message: 'Alias is already taken',
                          suggestion: response.suggestions[0],
                        }
                  },
                }}
                children={(field) => {
                  const isInvalid =
                    !field.state.meta.isValid &&
                    field.state.meta.errorSourceMap?.onDynamic &&
                    form.state.submissionAttempts > 0

                  const placeholder =
                    field.state.meta.errors[0] &&
                    'suggestion' in field.state.meta.errors[0]
                      ? field.state.meta.errors[0].suggestion
                      : 'pendant-publishing'

                  return (
                    <Field data-invalid={isInvalid} className={isInvalid ? '' : 'hidden'}>
                      <Input
                        name={field.name}
                        value={field.state.value}
                        onChange={(e) =>
                          field.handleChange(normalizeAlias(e.target.value))
                        }
                        onBlur={field.handleBlur}
                        placeholder={`e.g. ${placeholder}`}
                        aria-invalid={isInvalid}
                        variant="inverse"
                        size="lg"
                        autoComplete="off"
                      />
                      {isInvalid && <FieldError errors={field.state.meta.errors} />}
                    </Field>
                  )
                }}
              />

              <Button
                type="submit"
                variant="lime"
                size="lg"
                loading={form.state.isSubmitting || createMutation.isPending}
                loadingLabel="Creating organization..."
              >
                Create Organization
              </Button>

              <p className="text-accent-foreground text-center">
                By continuing, you&apos;re agreeing to our{' '}
                <a
                  href="https://happitu.com/legal/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://happitu.com/legal/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </form>
          </>
        )}
      </section>
    </SetupWizard>
  )
}

function normalizeAlias(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}
